import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Send, Check } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { endpoints } from "@/lib/api";
import { HERO } from "@/lib/heroImages";
import N8Emblem from "@/components/N8Emblem";

/**
 * The Mirror — a guided reflection experience.
 *
 * Phase 1: Full UX built against mocked AI responses via the backend
 * service layer. A real provider slots in later without any UI change.
 *
 * States managed on this page:
 *   idle       → opening screen (title + placeholder input + pathways)
 *   in_session → messages + input
 *   closing    → "Take This With You" summary + N8ONE content picks
 */

const PATHWAYS = [
  { key: "vent",       label: "I need to vent" },
  { key: "understand", label: "Help me understand what I'm feeling" },
  { key: "reframe",    label: "Help me see this differently" },
  { key: "decide",     label: "Help me work through a decision" },
  { key: "return",     label: "Help me come back to myself" },
];

const PLACEHOLDERS = [
  "Something has been bothering me…",
  "I don't know why I'm feeling this way…",
  "I need to talk something through…",
  "I feel stuck.",
  "I don't know what I need right now.",
];

export default function Mirror() {
  const nav = useNavigate();
  const [mode, setMode] = useState("idle"); // idle | in_session | closing
  const [seed, setSeed] = useState("");
  const [pathway, setPathway] = useState(null);
  const [placeholder, setPlaceholder] = useState(PLACEHOLDERS[0]);
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [limits, setLimits] = useState(null);
  const [takeAway, setTakeAway] = useState(null);
  const [finalContent, setFinalContent] = useState([]);
  const [limitBanner, setLimitBanner] = useState(null);
  const feedRef = useRef(null);

  // Rotate placeholders quietly.
  useEffect(() => {
    if (mode !== "idle") return;
    const t = setInterval(() => {
      setPlaceholder((p) => {
        const i = PLACEHOLDERS.indexOf(p);
        return PLACEHOLDERS[(i + 1) % PLACEHOLDERS.length];
      });
    }, 4000);
    return () => clearInterval(t);
  }, [mode]);

  // Auto-scroll on new message
  useEffect(() => {
    if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
  }, [messages, sending]);

  const beginSession = async () => {
    const seedText = seed.trim();
    const canBegin = seedText || pathway;
    if (!canBegin) return;
    try {
      const { data } = await endpoints.mirrorCreate({ pathway, seed_text: seedText });
      if (data.limit_reached) {
        setLimitBanner(data.message);
        return;
      }
      setSession({ id: data.id });
      setLimits(data.limits);
      setMode("in_session");
      if (seedText) {
        // Send the seed as the first message immediately.
        await sendMessage(seedText, { sessionId: data.id });
        setSeed("");
      }
    } catch {
      toast.error("Couldn't open The Mirror. Try again in a moment.");
    }
  };

  const sendMessage = async (text, opts = {}) => {
    const sid = opts.sessionId || session?.id;
    if (!sid || !text.trim() || sending) return;
    setSending(true);
    // Optimistic user message
    const userMsg = { id: `local-${Date.now()}`, role: "user", text, ts: new Date().toISOString() };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    try {
      const { data } = await endpoints.mirrorSend(sid, text);
      if (data.limit_reached) {
        setLimitBanner(data.message);
        return;
      }
      setMessages((prev) => [...prev, data.reply]);
    } catch {
      toast.error("The Mirror couldn't respond just now.");
    } finally {
      setSending(false);
    }
  };

  const closeSession = async () => {
    if (!session?.id) return;
    try {
      const { data } = await endpoints.mirrorClose(session.id);
      setTakeAway(data.take_away);
      setFinalContent(data.content || []);
      setMode("closing");
    } catch {
      toast.error("Couldn't gather your take-away.");
    }
  };

  const returnHome = () => nav("/");

  return (
    <div className="min-h-screen bg-[hsl(var(--bg))]" data-testid="mirror-page">
      {/* Cinematic hero — still water reflection, wash matches the ivory theme */}
      <section
        className="relative overflow-hidden"
        aria-hidden={mode !== "idle"}
        data-testid="mirror-hero"
      >
        <div
          className="absolute inset-0 bg-center bg-cover"
          style={{ backgroundImage: `url("${HERO.mirror}")` }}
          aria-hidden
        />
        <div
          className="absolute inset-0"
          aria-hidden
          style={{
            background:
              "linear-gradient(180deg, rgba(30,23,18,0.55) 0%, rgba(30,23,18,0.15) 60%, hsl(var(--bg)) 100%)",
          }}
        />
        <div className="relative max-w-3xl mx-auto px-5 sm:px-8 pt-10 sm:pt-14 pb-6 sm:pb-10">
          <button
            type="button"
            onClick={returnHome}
            data-testid="mirror-back"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] transition-colors"
            style={{ color: "rgba(246,241,232,0.85)" }}
          >
            <ArrowLeft size={12} /> Home
          </button>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-16 -mt-6 sm:-mt-10 relative">

        {mode === "idle" && (
          <IdleView
            seed={seed}
            setSeed={setSeed}
            pathway={pathway}
            setPathway={setPathway}
            placeholder={placeholder}
            beginSession={beginSession}
            limitBanner={limitBanner}
          />
        )}

        {mode === "in_session" && (
          <SessionView
            messages={messages}
            input={input}
            setInput={setInput}
            sending={sending}
            onSend={() => sendMessage(input)}
            onClose={closeSession}
            feedRef={feedRef}
            limitBanner={limitBanner}
          />
        )}

        {mode === "closing" && (
          <CloseView
            takeAway={takeAway}
            content={finalContent}
            onKeep={() => setMode("in_session")}
            onFinish={returnHome}
          />
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// Views
// ------------------------------------------------------------------

function IdleView({ seed, setSeed, pathway, setPathway, placeholder, beginSession, limitBanner }) {
  return (
    <div className="mt-14 space-y-10" data-testid="mirror-idle">
      <header>
        <div className="text-[11px] uppercase tracking-[0.32em] text-[hsl(var(--copper))]">
          The Mirror
        </div>
        <h1 className="mt-4 font-display text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight text-[hsl(var(--fg))]">
          You don't need to have the right words.
        </h1>
        <p className="mt-5 font-display italic text-xl sm:text-2xl text-[hsl(var(--clay))]">
          Start with whatever is here.
        </p>
      </header>

      {limitBanner ? (
        <div className="p-6 rounded-[20px] border hairline bg-[hsl(var(--sand))]/40" data-testid="mirror-limit-banner">
          <p className="font-display italic text-lg text-[hsl(var(--fg))]/85">{limitBanner}</p>
        </div>
      ) : (
        <>
          <Textarea
            value={seed}
            onChange={(e) => setSeed(e.target.value.slice(0, 2000))}
            placeholder={placeholder}
            rows={5}
            className="bg-transparent border-[hsl(var(--border))] focus-visible:ring-[hsl(var(--copper))] text-[15px] leading-relaxed"
            data-testid="mirror-seed-input"
          />

          <div>
            <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))] mb-3">
              Or begin with…
            </div>
            <div className="flex flex-wrap gap-2" data-testid="mirror-pathways">
              {PATHWAYS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPathway(pathway === p.key ? null : p.key)}
                  aria-pressed={pathway === p.key}
                  data-testid={`mirror-pathway-${p.key}`}
                  className={`px-4 py-2 rounded-full text-sm border hairline transition-all ${
                    pathway === p.key
                      ? "bg-[hsl(var(--copper))]/15 border-[hsl(var(--copper))]/50 text-[hsl(var(--fg))]"
                      : "text-[hsl(var(--fg))]/75 hover:bg-[hsl(var(--sand))]/50"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Button
              type="button"
              onClick={beginSession}
              disabled={!seed.trim() && !pathway}
              data-testid="mirror-begin"
              className="rounded-full bg-[hsl(var(--copper))] hover:bg-[hsl(var(--copper))]/90 text-[hsl(var(--paper))] px-7"
            >
              Begin
            </Button>
            <p className="mt-4 text-xs italic text-[hsl(var(--muted-foreground))] max-w-lg">
              The Mirror is a private reflection space, not a replacement for professional care. If
              you're in crisis, please reach out to someone equipped to help you today.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

function SessionView({ messages, input, setInput, sending, onSend, onClose, feedRef, limitBanner }) {
  return (
    <div className="mt-8 space-y-6" data-testid="mirror-session">
      <div
        ref={feedRef}
        data-testid="mirror-feed"
        className="max-h-[60vh] overflow-y-auto pr-1 space-y-8"
      >
        {messages.map((m, idx) => (
          <Message
            key={m.id}
            message={m}
            generating={sending && m.role === "mirror" && idx === messages.length - 1}
          />
        ))}
        {sending && (
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--muted-foreground))] italic" data-testid="mirror-thinking">
            <N8Emblem
              mode="simplified"
              size={26}
              breathing
              active
              ariaLabel="N8 Guide emblem — listening"
              testid="mirror-thinking-emblem"
              className="shrink-0"
            />
            The Mirror is listening…
          </div>
        )}
      </div>

      {limitBanner && (
        <div className="p-5 rounded-[20px] border hairline bg-[hsl(var(--sand))]/40" data-testid="mirror-limit-banner">
          <p className="font-display italic text-[hsl(var(--fg))]/85">{limitBanner}</p>
        </div>
      )}

      <div className="flex items-end gap-3 border-t hairline pt-6">
        <Textarea
          value={input}
          onChange={(e) => setInput(e.target.value.slice(0, 2000))}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              onSend();
            }
          }}
          rows={3}
          placeholder="Tell me more…"
          className="bg-transparent border-[hsl(var(--border))] focus-visible:ring-[hsl(var(--copper))]"
          data-testid="mirror-input"
        />
        <button
          type="button"
          onClick={onSend}
          disabled={!input.trim() || sending}
          data-testid="mirror-send"
          className="shrink-0 w-11 h-11 rounded-full bg-[hsl(var(--copper))] text-[hsl(var(--paper))] flex items-center justify-center disabled:opacity-40"
        >
          <Send size={16} />
        </button>
      </div>

      <div className="text-center">
        <button
          type="button"
          onClick={onClose}
          data-testid="mirror-close"
          className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--copper))]"
        >
          Take this with me →
        </button>
      </div>
    </div>
  );
}

function Message({ message, generating = false }) {
  const isMirror = message.role === "mirror";
  return (
    <div data-testid={`mirror-msg-${message.role}`} className={isMirror ? "flex items-start gap-3" : ""}>
      {/* Mirror replies are N8 Guide generated — carry the breathing
          emblem beside each assistant message. `generating` bumps to the
          brighter 5.5s cycle while the next reply is being streamed. */}
      {isMirror && (
        <N8Emblem
          mode="simplified"
          size={30}
          breathing
          active={generating}
          ariaLabel="N8 Guide emblem"
          testid={`mirror-msg-emblem-${message.id}`}
          className="shrink-0 mt-0.5"
        />
      )}
      <div className={isMirror ? "min-w-0 flex-1" : ""}>
        <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--muted-foreground))]/80 mb-2">
          {isMirror ? "The Mirror" : "You"}
        </div>
        <p className={
          isMirror
            ? "font-display italic text-lg md:text-xl text-[hsl(var(--fg))]/90 leading-snug max-w-2xl"
            : "text-[15.5px] text-[hsl(var(--fg))]/85 leading-relaxed max-w-2xl"
        }>
          {message.text}
        </p>
        {message.safety && (
          <p className="mt-3 text-xs italic text-[hsl(var(--terracotta))]">
            If you're in immediate danger, please reach out to emergency services now.
          </p>
        )}
        {isMirror && Array.isArray(message.content) && message.content.length > 0 && (
          <ContentPicks content={message.content} testid="mirror-turn-picks" />
        )}
      </div>
    </div>
  );
}

function ContentPicks({ content, testid }) {
  return (
    <div className="mt-5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--copper))] mb-3">
        Continue With
      </div>
      <ul className="space-y-2">
        {content.map((c) => (
          <li key={c.slug}>
            <a
              href={c.route}
              data-testid={`mirror-pick-${c.slug}`}
              className="inline-flex items-center gap-2 text-[14px] text-[hsl(var(--fg))]/85 hover:text-[hsl(var(--copper))] tab-link"
            >
              <span>{c.title}</span>
              <ArrowRight size={12} />
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CloseView({ takeAway, content, onKeep, onFinish }) {
  return (
    <div className="mt-14 space-y-10" data-testid="mirror-closing">
      <header>
        <div className="text-[11px] uppercase tracking-[0.32em] text-[hsl(var(--copper))]">
          Take This With You
        </div>
        <h2 className="mt-4 font-display text-3xl md:text-4xl leading-tight italic text-[hsl(var(--fg))]">
          A small thing to carry.
        </h2>
      </header>

      {takeAway && (
        <div className="space-y-8 max-w-2xl" data-testid="mirror-takeaway">
          <div className="flex items-start gap-3">
            {/* Guide-generated takeaway — a quiet breathing emblem
                signals N8 Guide authored this reflection. */}
            <N8Emblem
              mode="simplified"
              size={32}
              breathing
              ariaLabel="N8 Guide emblem"
              testid="mirror-takeaway-emblem"
              className="shrink-0 mt-1"
            />
            <div className="min-w-0 flex-1 space-y-8">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--muted-foreground))]">
                  What you seem to be recognizing
                </div>
                <p className="mt-3 font-display italic text-lg md:text-xl text-[hsl(var(--fg))]/90 leading-snug">
                  {takeAway.recognizing}
                </p>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--muted-foreground))]">
                  What may matter most right now
                </div>
                <p className="mt-3 font-display italic text-lg md:text-xl text-[hsl(var(--clay))] leading-snug">
                  {takeAway.matters_most}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {content?.length > 0 && (
        <div data-testid="mirror-final-picks">
          <div className="text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--copper))] mb-3">
            Continue With
          </div>
          <ul className="space-y-3">
            {content.map((c) => (
              <li key={c.slug}>
                <a
                  href={c.route}
                  data-testid={`mirror-final-pick-${c.slug}`}
                  className="inline-flex items-center gap-2 text-[15px] text-[hsl(var(--fg))]/85 hover:text-[hsl(var(--copper))] tab-link"
                >
                  <span>{c.title}</span>
                  <ArrowRight size={13} />
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex items-center gap-4 flex-wrap pt-4 border-t hairline">
        <Button
          type="button"
          onClick={onFinish}
          data-testid="mirror-finish"
          className="rounded-full bg-[hsl(var(--copper))] hover:bg-[hsl(var(--copper))]/90 text-[hsl(var(--paper))]"
        >
          <Check size={14} className="mr-1.5" /> I'm ready to continue
        </Button>
        <button
          type="button"
          onClick={onKeep}
          data-testid="mirror-keep"
          className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--copper))]"
        >
          I need a little more time here
        </button>
      </div>
    </div>
  );
}
